import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { getUser } from "./utils/storage";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GlobalDialog from "./components/GlobalDialog";
// Auth Pages
import Login from "./Pages/Auth/Login";
import Register from "./Pages/Auth/Register";
import SendEmailLink from "./Pages/Auth/SendEmailLink";
import PasswordResetForm from "./Pages/Auth/PasswordResetForm";

// Admin Pages
import Dashboard from "./Pages/Admin/Dashboard/Dashboard";
import UsersList from "./Pages/Admin/User/UsersList";
import UserForm from "./Pages/Admin/User/UserForm";
import ApproveUsers from "./Pages/Admin/User/ApproveUsers";
import ModelList from "./Pages/Admin/Models/ModelList";
import BrandList from "./Pages/Admin/Brands/BrandList";

// Client Pages
import ClientsList from "./Pages/Admin/Client/ClientsList";
import ClientForm from "./Pages/Admin/Client/ClientForm";
import ApproveClients from "./Pages/Admin/Client/ApproveClients";

// Device Pages
import WhiteStockList from "./Pages/Admin/Devices/WhiteStockList";
import BlueStockList from "./Pages/Admin/Devices/BlueStockList";
import GreenStockList from "./Pages/Admin/Devices/GreenStockList";
import ReserveStock from "./Pages/Admin/Devices/ReserveStock";
import ReservedStock from "./Pages/Admin/Devices/ReservedStock";
import SoldStock from "./Pages/Admin/Devices/SoldStock";
import RecycledStockHistory from "./Pages/Admin/Devices/RecycledStockHistory";
import SparePartsStock from "./Pages/Admin/Devices/SparePartsStock";
import StockList from "./Pages/Admin/Devices/StockList";
import DeviceStockList from "./Pages/Admin/Devices/DeviceStockList";
// General Pages
import Home from "./Pages/Home";
import Device from "./Pages/General/Device";
import DeviceForm from "./Pages/General/DeviceForm";
import DeviceAutoForm from "./Pages/General/DeviceAutoForm";
import MultiDeviceDetails from "./Pages/General/MultiDeviceDetails";

// Report Pages
import GreenStockReport from "./Pages/Admin/Reports/GreenStockReport";
import BlueStockReport from "./Pages/Admin/Reports/BlueStockReport";
import WhiteStockReport from "./Pages/Admin/Reports/WhiteStockReport";
import ClientReport from "./Pages/Admin/Reports/ClientReport";
import SoldStockReport from "./Pages/Admin/Reports/SoldStockReport";
import TotalQuantityReport from "./Pages/Admin/Reports/TotalQuantity";
import InvoiceReport from "./Pages/Admin/Reports/InvoiceReport";
import AdminSidebar from "./components/AdminSidebar";
import ReservedStockReport from "./Pages/Admin/Reports/ReservedStockReport";

// Trash Pages
import TrashDevices from "./Pages/Admin/Trash/TrashDevices";
import TrashCategories from "./Pages/Admin/Trash/TrashCategories";
import TrashBrands from "./Pages/Admin/Trash/TrashBrands";
import TrashModels from "./Pages/Admin/Trash/TrashModels";
import TrashUsers from "./Pages/Admin/Trash/TrashUsers";
import TrashClients from "./Pages/Admin/Trash/TrashClients";

const queryClient = new QueryClient();

function App() {
  const user = getUser();

  // Helper function to create protected routes
  const ProtectedRoute = ({ element, roles = [] }) => {
    if (!user) return <Navigate to="/" />;
    if (
      roles.length &&
      !roles.map((r) => r.toLowerCase()).includes(user.role?.toLowerCase())
    )
      return <Navigate to="/" />;
    return element;
  };

  // Admin & Stock Manager roles
  const adminManagerRoles = ["Admin", "SuperAdmin", "Stock Manager"];

  // Technician roles - can access device registration and stock viewing
  const allRoles = ["Admin", "SuperAdmin", "Stock Manager", "Technician"];

  return (
    <div className="App">
      <QueryClientProvider client={queryClient}>
        <ToastContainer position="top-center" />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/forgot-password" element={<SendEmailLink />} />
            <Route
              path="/reset-password/:token"
              element={<PasswordResetForm />}
            />

            {!user ? (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/admin/devices" />} />
            )}

            {/* Admin Only Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute
                  element={<Dashboard />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute
                  element={<UsersList />}
                  roles={["Admin", "SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/users/pending"
              element={
                <ProtectedRoute
                  element={<ApproveUsers />}
                  roles={["Admin", "SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/users/form"
              element={
                <ProtectedRoute
                  element={<UserForm />}
                  roles={["Admin", "SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/users/:userId/edit"
              element={
                <ProtectedRoute
                  element={<UserForm />}
                  roles={["Admin", "SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/device-models"
              element={
                <ProtectedRoute
                  element={<ModelList />}
                  roles={["Admin", "SuperAdmin", "Stock Manager", "Technician"]}
                />
              }
            />
            <Route
              path="/admin/device-brands"
              element={
                <ProtectedRoute
                  element={<BrandList />}
                  roles={["Admin", "SuperAdmin", "Stock Manager", "Technician"]}
                />
              }
            />
            <Route
              path="/admin/reports/green"
              element={
                <ProtectedRoute
                  element={<GreenStockReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/total-quantity"
              element={
                <ProtectedRoute
                  element={<TotalQuantityReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/blue"
              element={
                <ProtectedRoute
                  element={<BlueStockReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/white"
              element={
                <ProtectedRoute
                  element={<WhiteStockReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/client"
              element={
                <ProtectedRoute
                  element={<ClientReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/invoice"
              element={
                <ProtectedRoute
                  element={<InvoiceReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />

            <Route
              path="/admin/reports/reserved"
              element={
                <ProtectedRoute
                  element={<ReservedStockReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/sold"
              element={
                <ProtectedRoute
                  element={<SoldStockReport />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/reports/recycled"
              element={
                <ProtectedRoute
                  element={<RecycledStockHistory />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/spare-parts"
              element={
                <ProtectedRoute
                  element={<SparePartsStock />}
                  roles={allRoles}
                />
              }
            />

            {/* Admin & Stock Manager Routes */}

            <Route
              path="/admin/devices"
              element={
                <ProtectedRoute
                  element={<DeviceStockList />}
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/admin/devices/white-stock"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <WhiteStockList />
                    </AdminSidebar>
                  }
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/admin/devices/blue-stock"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <BlueStockList />
                    </AdminSidebar>
                  }
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/admin/devices/green-stock"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <GreenStockList />
                    </AdminSidebar>
                  }
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/admin/stock/reserve"
              element={
                <ProtectedRoute
                  element={<ReserveStock />}
                  roles={adminManagerRoles}
                />
              }
            />
            <Route
              path="/admin/stock/reserved"
              element={
                <ProtectedRoute
                  element={<ReservedStock />}
                  roles={adminManagerRoles}
                />
              }
            />
            <Route
              path="/admin/stock/sold"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <SoldStock />
                    </AdminSidebar>
                  }
                  roles={adminManagerRoles}
                />
              }
            />
            <Route
              path="/admin/stock/history/recycled"
              element={
                <ProtectedRoute
                  element={<RecycledStockHistory />}
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/stock/list"
              element={
                <ProtectedRoute
                  element={<StockList />}
                  roles={adminManagerRoles}
                />
              }
            />

            {/* Authenticated User Routes */}

            <Route
              path="/devices/form/:params?"
              element={
                <ProtectedRoute element={<DeviceForm />} roles={allRoles} />
              }
            />
            <Route
              path="/devices/form/:objID/edit"
              element={
                <ProtectedRoute element={<DeviceForm />} roles={allRoles} />
              }
            />
            <Route
              path="/devices/:objID/details"
              element={<ProtectedRoute element={<Device />} roles={allRoles} />}
            />
            <Route
              path="/devices/multi-device-details"
              element={
                <ProtectedRoute
                  element={<MultiDeviceDetails />}
                  roles={allRoles}
                />
              }
            />
            <Route
              path="/devices/autoform/:params?"
              element={
                <ProtectedRoute element={<DeviceForm />} roles={allRoles} />
              }
            />
            <Route
              path="/devices/autoform/:objID/edit"
              element={
                <ProtectedRoute element={<DeviceForm />} roles={allRoles} />
              }
            />

            {/* Client Routes */}
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute
                  element={<ClientsList />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/clients/pending"
              element={
                <ProtectedRoute
                  element={<ApproveClients />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
            <Route
              path="/admin/clients/form"
              element={
                <ProtectedRoute
                  element={<ClientForm />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />

            <Route
              path="/admin/trash"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <TrashCategories />
                    </AdminSidebar>
                  }
                  roles={["SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/trash/devices"
              element={
                <ProtectedRoute
                  element={
                    <AdminSidebar>
                      <TrashDevices />
                    </AdminSidebar>
                  }
                  roles={["Admin", "SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/trash/brands"
              element={
                <ProtectedRoute
                  element={<TrashBrands />}
                  roles={["SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/trash/models"
              element={
                <ProtectedRoute
                  element={<TrashModels />}
                  roles={["SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/trash/users"
              element={
                <ProtectedRoute
                  element={<TrashUsers />}
                  roles={["SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/trash/clients"
              element={
                <ProtectedRoute
                  element={<TrashClients />}
                  roles={["SuperAdmin"]}
                />
              }
            />
            <Route
              path="/admin/clients/:clientId/edit"
              element={
                <ProtectedRoute
                  element={<ClientForm />}
                  roles={["Admin", "SuperAdmin", "Stock Manager"]}
                />
              }
            />
          </Routes>
          <GlobalDialog />
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </div>
  );
}

export default App;
